import React, {useEffect, useRef, useState} from 'react';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {upsertSite} from 'api/SitesApi';
import {useAppContext} from 'contexts/AppContext';
import {isCustomerAdmin, isSystemAdmin} from 'utils/Auth';
import {isEmpty, isSwitchOn} from 'utils/Utils';
import SiteForm from './SiteForm';

const DEFAULT_INSTANCE = {
    id: '',
    site_id: '',
    site_name: '',
    site_address: '',
    active: ['1'],
};
const VALIDATION_RULES = {
    site_id: Yup.string().required('Site ID is required'),
    site_name: Yup.string().required('Site name is required'),
    site_address: Yup.string().required('Site address is required'),
};

export default function SiteDetails({site, useCustomer, customers, onCloseClick}) {
    const isMounted = useRef(false);
    const {setPageTitle, showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({...DEFAULT_INSTANCE});
    const isEditable = isSystemAdmin() || isCustomerAdmin();
    const validationRules = {...VALIDATION_RULES};
    if (useCustomer) validationRules.customer_uuid = Yup.string().required('Customer is required');
    const validationSchema = Yup.object().shape(validationRules);

    useEffect(() => {
        isMounted.current = true;
        setPageTitle(isEmpty(site) ? 'New site' : 'Site');
        getInitialValues();
        return () => isMounted.current = false;
    }, []);

    const getInitialValues = () => {
        if (isMounted.current) {
            setInitialValues(oldData => {
                const newData = {};
                for (const [key, value] of Object.entries(oldData)) {
                    newData[key] = site.hasOwnProperty(key) ? site[key] : value;
                }
                if (useCustomer) newData.customer_uuid = site.customer ? site.customer.customer_uuid : '';
                newData.active = newData.active ? ['1'] : [];
                return newData;
            });
        }
    };

    const handleSubmit = (values, {setSubmitting, setErrors}) => {
        setIsLoading(true);
        const payload = {...values};
        if (!useCustomer) delete payload.customer_uuid;
        payload.active = isSwitchOn(payload.active);
        const onError = error => handleFailureResponse(error, setErrors);
        const onDone = () => {
            if (isMounted.current) {
                setSubmitting(false);
                setIsLoading(false);
            }
        };
        upsertSite(payload, handleSuccessResponse, onError, onDone);
    };

    const handleSuccessResponse = (response) => {
        showMessage('success', 'Site has been successfully saved');
        onCloseClick();
    };

    const handleFailureResponse = (error, setErrors) => {
        handleApiError(error);
        if (isMounted.current && error.response) setErrors(error.response.data.errors);
    };

    return (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
            {(props) => (
                <SiteForm
                  {...props}
                  useCustomer={useCustomer}
                  customers={customers}
                  isEditable={isEditable}
                  isLoading={isLoading}
                  onCloseClick={onCloseClick}
                />
            )}
        </Formik>
    );
}
